import {
    useSetPrimaryImage,
    useSetSecondaryImage,
} from '~/hooks/image/useSelectedImages';
import { useResetRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import {
    getContourUndoService,
    getRoiBaselineStore,
} from '~/domain/ServiceContainer';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { setActiveSession } from '~/domain/session/activeSession';
import { abortActiveSliceStream } from '~/services/imageStreaming/activeSliceStream';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { useClearAnnotations } from '~/hooks/useClearAnnotations';
import {
    hasUnsavedChangesState,
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { useClearMultiSelection } from '~/hooks/structure-set/useClearMultiSelection';
import {
    activeStructurePeekState,
    autoShownRoiIdState,
    selectedROIState,
    structureIsolationActiveState,
} from '~/state/atoms/structureSet/roiAtoms';
import { selectedRegistrationState } from '~/state/atoms/registration/registrationAtoms';
import {
    differenceReferenceModeState,
    selectedToolState,
} from '~/state/atoms/toolsAtoms';
import { useResetDoseDisplaySettings } from '~/hooks/dose/useDoseDisplaySettings';
import { optimizationObjectivesState } from '~/state/atoms/dose/optimizationObjectivesAtom';
import {
    adjacentSliceGhostsEnabledState,
    exportableItemsCountState,
    reviewedBurnedInImagesState,
    petMipRowSeriesState,
    petMipViewSettingsState,
    roiPreview3DOpenState,
    seriesMipPreviewVisibleState,
} from '~/state/atoms/viewerAtoms';
import { connectorPatientManifestState } from '~/state/atoms/connectorManifestAtoms';
import { openedScopeState } from '~/hooks/openInViewer/openedScopeAtom';
import { couchTopRowsState } from '~/state/atoms/couchTopAtoms';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import { handleError } from '~/utils/errors';
import { closeSnackbar } from 'notistack';
import { jobQueueState } from '~/state/atoms/jobAtoms';
import { useSetCurrentDose } from '~/hooks/dose/useCurrentDose';

/**
 * Hook that provides a function to fully unload the current patient,
 * clearing all associated state from the viewport, repositories, and atoms.
 *
 * Used when switching to a different patient in clinical mode.
 */
export default function useUnloadPatient() {
    const patientRepository = useRecoilValue(patientRepositoryState);
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const session = useSessionOrNull();

    const setSelectedPatientId = useSetRecoilState(selectedPatientIdState);
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);
    const setPrimaryImage = useSetPrimaryImage();
    const setSecondaryImage = useSetSecondaryImage();
    const setCurrentStructureSet = useSetRecoilState(currentStructureSetState);
    const setCurrentDose = useSetCurrentDose();
    const setSelectedROI = useSetRecoilState(selectedROIState);
    const setAutoShown = useSetRecoilState(autoShownRoiIdState);
    const clearMultiSelection = useClearMultiSelection();
    const setRoiPreviewOpen = useSetRecoilState(roiPreview3DOpenState);
    const setAdjacentSliceGhostsEnabled = useSetRecoilState(
        adjacentSliceGhostsEnabledState
    );
    const setStructureIsolationActive = useSetRecoilState(
        structureIsolationActiveState
    );
    const setActiveStructurePeek = useSetRecoilState(activeStructurePeekState);
    const setSeriesMipPreviewVisible = useSetRecoilState(
        seriesMipPreviewVisibleState
    );
    const setPetMipRowSeries = useSetRecoilState(petMipRowSeriesState);
    const resetPetMipViewSettings = useResetRecoilState(
        petMipViewSettingsState
    );
    const setSelectedRegistration = useSetRecoilState(
        selectedRegistrationState
    );
    const setTool = useSetRecoilState(selectedToolState);
    const setDifferenceReferenceMode = useSetRecoilState(
        differenceReferenceModeState
    );
    const setExportableItemsCount = useSetRecoilState(
        exportableItemsCountState
    );
    const setOptimizationObjectives = useSetRecoilState(
        optimizationObjectivesState
    );
    const resetDoseDisplaySettings = useResetDoseDisplaySettings();
    const setJobQueue = useSetRecoilState(jobQueueState);
    const setReviewedBurnedInImages = useSetRecoilState(
        reviewedBurnedInImagesState
    );
    const setConnectorManifest = useSetRecoilState(
        connectorPatientManifestState
    );
    const clearAnnotations = useClearAnnotations();
    const resetCouchTopRows = useResetRecoilState(couchTopRowsState);
    const resetOpenedScope = useResetRecoilState(openedScopeState);

    /**
     * @param options.preserveDicomProcessor When true, the DicomProcessor's
     *   internal state (parsed patients and processed SOP UIDs) is kept
     *   intact. This is needed when switching patients — the processor
     *   still holds the incoming patient data that must be committed to
     *   the repository after the current patient is unloaded.
     */
    function unloadCurrentPatient(
        options: { preserveDicomProcessor?: boolean } = {}
    ): void {
        // Stop any progressive image retrieval FIRST and synchronously: a
        // slice landing after this point would be written into a volume that
        // no longer belongs to a loaded patient, and pushed into an engine
        // texture that is about to be deleted.
        abortActiveSliceStream();

        // Grid contour edits live only in registry-held Environments:
        // persist them before any teardown, then drop the registry so no
        // entry (or resident texture) survives into the next patient. A
        // failed save is surfaced but never blocks the unload.
        const datasetRegistry = getDatasetEnvironmentRegistry();
        try {
            datasetRegistry.saveAllResidentChanges();
        } catch (error) {
            handleError(
                error,
                'Failed to save grid contour edits before unloading the patient.'
            );
        }
        datasetRegistry.clear();

        // Clear Recoil UI atoms
        setPrimaryImage(undefined);
        setSecondaryImage(undefined);
        // Drop the user's burned-in-annotation attestations: each is a statement
        // about one specific image of this patient, and must never be inherited
        // by the next patient's scans.
        setReviewedBurnedInImages(new Set());
        // Every measure shape and draft holds values read from this patient's
        // images; none may be shown against the next patient's.
        clearAnnotations();
        // Couch-top rows are keyed by series uid, so the next patient could
        // never read one back — but a Recoil atom outlives the session and
        // would otherwise accumulate every patient's placements for the
        // lifetime of the app.
        resetCouchTopRows();
        setCurrentStructureSet(undefined);
        setCurrentDose(undefined);
        setSelectedROI(null);
        setAutoShown(null);
        clearMultiSelection();
        setRoiPreviewOpen(false);
        setAdjacentSliceGhostsEnabled(false);
        // Isolate follows the selection, which is cleared just above, so it
        // would sit dormant and then silently re-dim the moment the user
        // picks a structure in the NEXT patient.
        setStructureIsolationActive(false);
        // A peek is held on a structure of the patient unloaded above, so
        // no hold carries over to the next one.
        setActiveStructurePeek(null);
        // Like the 3D preview: an overview opened for THIS patient must not
        // auto-open on the next one — where it would immediately start the
        // whole-volume projection pass unrequested.
        setSeriesMipPreviewVisible(false);
        // The next patient must not inherit the PET projection or its angle.
        setPetMipRowSeries(null);
        resetPetMipViewSettings();
        // Selection now lives in a UI atom (the retired RegistrationStore used
        // to reset it on patient/primary change); clear it here so a sopUid
        // from the previous patient cannot linger across a patient switch.
        setSelectedRegistration(null);
        setTool('pan');
        // Reset the difference-overlay mode so its green/red diff cannot persist
        // onto the next patient's selected structure — where it would read as an
        // edit that never happened. The always-mounted useReferenceDiff turns
        // the engine cross-slice shader off once it observes 'off'.
        setDifferenceReferenceMode('off');

        // Clear undo history
        const undoService = getContourUndoService();
        undoService.clear();

        // Clear the per-ROI original-AI baselines captured this session.
        getRoiBaselineStore().clear();

        // Tear down the active PatientSession synchronously. The
        // PatientSessionProvider's effect cleanup will also dispose
        // on the next render once the patient prop becomes null, but
        // we want the change-tracker / engine-adapter wiring gone
        // before the synchronous teardown below runs, and before any
        // engine event can fire on stale references. dispose() is
        // idempotent so the later cleanup is a safe no-op.
        if (session) {
            session.dispose();
        }
        setActiveSession(null);

        // Remove all patients from the in-memory patient repository
        for (const patient of patientRepository.getAll()) {
            patientRepository.delete(patient.id);
        }

        // Clear processed SOP UIDs from the DICOM processor so
        // re-importing the same data later is not blocked.
        // Skipped when switching patients — the processor still holds
        // the incoming patient data that toRepository() needs.
        if (!options.preserveDicomProcessor) {
            dicomProcessor.clearProcessedState();
        }

        // Reset global tracking atoms. The connector manifest is cleared
        // after the selection (both land in the same React batch): the
        // session must never be recreated as patient + null manifest.
        setSelectedPatientId(null);
        setConnectorManifest(null);
        resetOpenedScope();
        setHasUnsavedChanges(false);
        setExportableItemsCount(0);
        setOptimizationObjectives([]);
        // Per-dose display settings are keyed by a per-session DoseId, so
        // the next patient's doses can never read these back — left in
        // place they would only accumulate the previous patients' colorwash
        // windows and prescriptions for the lifetime of the app.
        resetDoseDisplaySettings();
        setJobQueue((prev) => {
            if (prev.snackbarId) {
                closeSnackbar(prev.snackbarId);
            }
            return { snackbarId: undefined, jobs: [] };
        });
    }

    return { unloadCurrentPatient };
}
